import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, EventEmitter, Output, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  FormGroup,
  FormBuilder,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { CaptchaService } from '../../../services/captcha/captcha.service';
import { Observable } from 'rxjs/internal/Observable';
import { environment } from '../../../../environments/environment';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  receiptDateValidator,
  receiptNumberValidator,
  receiptTotalValidator,
  sellerBrnValidator,
} from '../../../shared/Validators/receipt-details-validators';
import { DialogComponent } from '../../../shared/components/dialog/dialog.component';

@Component({
  selector: 'app-receipt-details',
  templateUrl: './receipt-details.component.html',
  styleUrl: './receipt-details.component.scss',
  providers: [DatePipe],
  standalone: false
})
export class ReceiptDetailsComponent {
  private readonly isFormSubmittedState = signal(false);
  private readonly captchaImageState = signal<string | null>(null);
  private readonly validationMessageState = signal('');
  private readonly captchaIdState = signal<string | null>(null);
  private readonly captchaInputInvalidState = signal(false);
  private readonly disablePTTestingState = signal<string | null>(null);

  get isFormSubmitted() { return this.isFormSubmittedState(); }
  set isFormSubmitted(value: boolean) { this.isFormSubmittedState.set(value); }
  get captchaImage() { return this.captchaImageState(); }
  set captchaImage(value: string | null) { this.captchaImageState.set(value); }
  get validationMessage() { return this.validationMessageState(); }
  set validationMessage(value: string) { this.validationMessageState.set(value); }
  get captchaId() { return this.captchaIdState(); }
  set captchaId(value: string | null) { this.captchaIdState.set(value); }
  get captchaInputInvalid() { return this.captchaInputInvalidState(); }
  set captchaInputInvalid(value: boolean) { this.captchaInputInvalidState.set(value); }
  get disablePTTesting() { return this.disablePTTestingState(); }
  set disablePTTesting(value: string | null) { this.disablePTTestingState.set(value); }

  @Output() formSubmitted = new EventEmitter<any>();
  receiptForm!: FormGroup;

  dialogRef: DynamicDialogRef | null = null;

  receiptTypes = [
    { name: 'Invoice', value: '01' },
    { name: 'Credit Note', value: '02' },
    { name: 'Debit Note', value: '03' },
    { name: 'Refund Note', value: '04' },
  ];

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private captchaService: CaptchaService,
    private datePipe: DatePipe,
    private dialogService: DialogService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.createForm();
    this.loadCaptcha();
  }
  ngOnInit() {
    this.receiptForm.get('captcha')?.valueChanges.subscribe(() => {
      const captchaControl = this.receiptForm.get('captcha');
      if (captchaControl?.errors?.['incorrect']) {
        captchaControl.setErrors(null); // Clear the incorrect error
        captchaControl.updateValueAndValidity(); // Trigger validation update
      }
    });
  }

  createForm() {
    this.receiptForm = this.fb.group({
      sellerBRN: ['', [Validators.required, sellerBrnValidator]],
      receiptType: ['', [Validators.required]],
      receiptNumber: ['', [Validators.required, receiptNumberValidator]],
      receiptDate: [null, [Validators.required, receiptDateValidator]],
      receiptTotal: ['', [Validators.required, receiptTotalValidator]],
      captcha: ['', [Validators.required]],
    });
  }

  loadCaptcha() {
    const isLocal = environment.production === false;
    this.captchaService.loadCaptcha().subscribe(
      (response: any) => this.handleCaptchaResponse(response),
      (error) => {
        console.error('Error loading CAPTCHA:', error);
      }
    );
  }

  refreshCaptcha() {
    this.captchaService.refreshCaptcha(this.captchaId).subscribe(
      (response) => this.handleCaptchaResponse(response),
      (error) => {
        console.error('Error loading CAPTCHA:', error);
      }
    );
    // this.loadCaptcha();
    this.receiptForm.get('captcha')?.reset();
    this.captchaInputInvalid = false;
  }

  validateCaptcha(): Observable<any> {
    const captchaValue = this.receiptForm.get('captcha')?.value;
    return this.captchaService.validateCaptcha(
      btoa(captchaValue),
      this.captchaId
    );
  }

  onSubmit() {
    if (this.receiptForm.invalid) {
      return;
    }

    this.validateCaptcha().subscribe(
      (response: any) => {
        if (response.status === 'success') {
          this.submitForm();
        } else {
          this.validationMessage = 'Captcha is incorrect'; // Set the error message
          this.captchaInputInvalid = true; // Flag as invalid
          this.receiptForm.get('captcha')?.setErrors({ incorrect: true }); // Set a custom error
        }
      },
      (error) => {
        this.validationMessage = 'An error occurred during CAPTCHA validation.';
        this.openErrorDialogue(this.validationMessage);
      }
    );
  }

  submitForm() {
    console.log('Form data:', this.receiptForm.value);

    const receiptDateFormatted = new Date(this.receiptForm.value.receiptDate);
    const recDate = this.datePipe.transform(receiptDateFormatted, 'yyyy-MM-dd');

    this.captchaService
      .receiptLookup(
        this.receiptForm.value.sellerBRN,
        this.receiptForm.value.receiptType,
        this.receiptForm.value.receiptNumber,
        recDate || '',
        //"2024-05-05",
        this.receiptForm.value.receiptTotal
      )
      .subscribe((response: any) => {
        console.log('Receipt lookup response:', response.lookUpResponse);
        console.log('Receipt groupCode:', response.groupCode);
        console.log('Receipt sellerName:', response.sellerName);

        if (response.lookUpResponse === 'Success') {
          console.log('Receipt lookup successful');
          const responseObj = {
            sellerLegalName: response.sellerName,
            value: 1,
          };
          this.formSubmitted.emit (responseObj);
          this.isFormSubmitted = true;
          this.disableFormFields();
        } else {

          console.log('Receipt lookup failed with Error -->');
          console.log('respErrorMessage:', response.respErrMsg);
          let respErrorMessage = response.respErrMsg;
          this.openErrorDialogue(respErrorMessage);
          // this
          //   .openErrorDialogue(`No receipt was found based on the provided data.
          //   Please carefully validate the data and try again.`);
          console.error('Receipt lookup failed');
          this.isFormSubmitted = false;
          this.refreshCaptcha();
        }
      });
    
    // const responseObj = {
    //   sellerLegalName: 'ABCSSD ABCSSD ABCSSD ',
    //   value: 1,
    // };
    // this.formSubmitted.emit(responseObj);
    // this.isFormSubmitted = true;
    // this.disableFormFields();
  }

  disableFormFields() {
    Object.keys(this.receiptForm.controls).forEach((key) => {
      this.receiptForm.get(key)?.disable();
    });
  }
  getFormData() {
    return this.receiptForm.value;
  }
  get f() {
    return this.receiptForm.controls;
  }

  resetForm() {
    this.receiptForm.reset();
    this.isFormSubmitted = false;
    this.validationMessage = '';
    Object.keys(this.receiptForm.controls).forEach((key) => {
      const control = this.receiptForm.get(key);
      if (control) {
        control.enable();
        control.markAsPristine();
        control.markAsUntouched();
      }
    });
    this.loadCaptcha();
    this.captchaInputInvalid = false;
  }

  handleCaptchaResponse(response: any) {
    if (response && response.captchaImage) {
      if(response.disablePTTesting != "1"){
        this.captchaImage = 'data:image/png;base64,' + response.captchaImage;
        this.captchaId = response.captchaId;
        this.disablePTTesting = response.disablePTTesting;
      }else{
        this.captchaImage = '';
        this.captchaId = '';
        this.disablePTTesting = response.disablePTTesting;
        this.receiptForm.get('captcha')?.setValue("CSDF234")
      }
      this.changeDetectorRef.detectChanges();
      console.log('Captcha loaded successfully:', response);
    } else {
      console.error('No CAPTCHA image in response');
    }
  }

  openErrorDialogue(message: string) {
    this.dialogRef = this.dialogService.open(DialogComponent, {
      header: 'Error',
      width: '600px',
      data: {
        title: '',
        messages: [message],
      },
      contentStyle: { 'max-height': '700px', overflow: 'auto' },
    });
  }
}